/**
 * Secure Storage Utility
 * Provides secure token storage with fallback mechanisms
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:7000/api' : '/api');

class SecureStorage {
  constructor() {
    this.isSecureContext = window.isSecureContext;
    this.tokenKey = 'brokerage_app_token';
    this.userKey = 'brokerage_app_user';
    this.refreshTokenKey = 'brokerage_app_refresh';
  }

  /**
   * Store token securely
   * @param {string} token - JWT token
   * @param {Object} user - User data
   */
  async setAuthData(token, user) {
    console.log('SecureStorage: Setting auth data via secure cookies:', { 
      hasToken: !!token, 
      user: user?.email
    });
    
    // Always store user data in localStorage first (safe, non-sensitive data)
    localStorage.setItem(this.userKey, JSON.stringify(this.sanitizeUserData(user)));
    console.log('SecureStorage: User data stored in localStorage');
    
    try {
      // Clear any existing fallback tokens first
      localStorage.removeItem(this.tokenKey);  // legacy cleanup
      sessionStorage.removeItem(this.tokenKey);
      delete this.memoryStorage;
      
      // Call backend endpoint to set secure httpOnly cookie
      await this.setSecureCookie(token);
      
      // Verify the cookie was actually stored by the browser
      const cookieStored = await this.verifyCookie();
      if (!cookieStored) {
        console.warn('SecureStorage: Cookie was not stored by browser, using sessionStorage fallback');
        sessionStorage.setItem(this.tokenKey, this.encryptToken(token));
      } else {
        console.log('SecureStorage: Secure cookie set and verified successfully');
      }
      
    } catch (error) {
      console.error('SecureStorage: Cookie setting failed, using sessionStorage fallback:', error);
      // Fallback to sessionStorage with encryption if cookie fails (scoped to this tab/session)
      sessionStorage.setItem(this.tokenKey, this.encryptToken(token));
      console.log('SecureStorage: Fallback to encrypted sessionStorage token');
    }
  }

  /**
   * Get stored token
   * @returns {string|null} Token or null if not found
   */
  getToken() {
    try {
      // console.log('🔍 SecureStorage: Getting token...');
      
      // Check if we have user data (indicates an active session)
      const userData = localStorage.getItem(this.userKey);
      // console.log('📦 SecureStorage: User data in localStorage:', !!userData);
      
      if (userData) {
        // Check if we have a cookie-based session first
        const encryptedToken = sessionStorage.getItem(this.tokenKey);
        
        if (!encryptedToken) {
          // No sessionStorage token, assume cookie-based auth
          // console.log('🍪 SecureStorage: Using cookie-based authentication');
          return 'cookie-auth';
        } else {
          // Have both user data and token, use sessionStorage token
          // console.log('🔑 SecureStorage: Using sessionStorage token');
          return this.decryptToken(encryptedToken);
        }
      }
      
      // Fallback: check for standalone encrypted token
      const encryptedToken = sessionStorage.getItem(this.tokenKey);
      if (encryptedToken) {
        // console.log('🔄 SecureStorage: Using fallback sessionStorage token');
        return this.decryptToken(encryptedToken);
      }
      
      // console.log('❌ SecureStorage: No token found');
      return null;
    } catch (error) {
      console.error('❌ SecureStorage: Error retrieving token:', error);
      return this.memoryStorage?.token || null;
    }
  }

  /**
   * Get stored user data
   * @returns {Object|null} User data or null if not found
   */
  async getUserData() {
    try {
      // console.log('👤 SecureStorage: Getting user data...');
      const userData = localStorage.getItem(this.userKey);
      
      if (userData) {
        // Before returning user data, validate that we have a valid token
        const token = this.getToken();
        
        if (!token) {
          // No valid token found, clear user data and return null
          console.log('❌ SecureStorage: No valid token found, clearing user data');
          await this.clearAuthData();
          return null;
        }
        
        // If token is cookie-based, verify the cookie exists and is valid
        if (token === 'cookie-auth') {
          // Access cookie is short-lived; a successful refresh already proves the session.
          const isCookieValid = (await this.verifyCookie()) || (await this.refreshSession());
          if (!isCookieValid) {
            console.log('❌ SecureStorage: Cookie-based auth invalid, clearing user data');
            await this.clearAuthData();
            return null;
          }
        } else {
          // If token is localStorage-based, check if it's expired
          const isExpired = this.isTokenExpired(token);
          if (isExpired) {
            console.log('❌ SecureStorage: Token expired, clearing user data');
            await this.clearAuthData();
            return null;
          }
        }
        
        const parsed = JSON.parse(userData);
        // console.log('✅ SecureStorage: Found user data for:', parsed?.email);
        return parsed;
      } else {
        // console.log('❌ SecureStorage: No user data found');
        return null;
      }
    } catch (error) {
      console.error('❌ SecureStorage: Error retrieving user data:', error);
      return this.memoryStorage?.user || null;
    }
  }

  /**
   * Clear all auth data
   */
  async clearAuthData() {
    // Clear all storage (guaranteed synchronous)
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.tokenKey);  // clean up any legacy localStorage tokens
    localStorage.removeItem(this.userKey);
    
    // Clear memory storage
    delete this.memoryStorage;
    
    // Clear any client-accessible cookies as safety net
    // This handles non-httpOnly fallback cookies or any stale auth cookies
    this.clearClientCookies();
    
    // Try to clear httpOnly cookie via backend endpoint
    try {
      await this.clearSecureCookie();
    } catch (error) {
      console.error('SecureStorage: Failed to clear httpOnly cookie via backend:', error);
    }
    
    console.log('SecureStorage: All auth data cleared including cookies');
  }

  /**
   * Clear all auth-related client-accessible cookies
   * This is a safety net - httpOnly cookies can only be cleared by the server,
   * but this handles any non-httpOnly fallback scenarios
   */
  clearClientCookies() {
    const cookiesToClear = ['auth_token', 'brokerage_app_token'];
    const paths = ['/', '/api'];
    
    for (const name of cookiesToClear) {
      for (const path of paths) {
        // Clear with various path/domain combinations to cover all cases
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; secure;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; samesite=lax;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; secure; samesite=lax;`;
      }
    }
  }

  /**
   * Simple encryption for token (basic obfuscation)
   * Note: This is not true encryption, just obfuscation
   * @param {string} token 
   * @returns {string} Obfuscated token
   */
  encryptToken(token) {
    // Simple base64 encoding with timestamp for basic obfuscation
    const timestamp = Date.now().toString();
    const combined = `${timestamp}:${token}`;
    return btoa(combined);
  }

  /**
   * Decrypt/deobfuscate token
   * @param {string} encryptedToken 
   * @returns {string} Original token
   */
  decryptToken(encryptedToken) {
    try {
      const decoded = atob(encryptedToken);
      const [timestamp, token] = decoded.split(':');
      
      // Check if token is too old (24 hours)
      const tokenAge = Date.now() - parseInt(timestamp);
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      if (tokenAge > maxAge) {
        this.clearAuthData();
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('Error decrypting token:', error);
      return null;
    }
  }

  /**
   * Check if a JWT token is expired
   * @param {string} token - JWT token
   * @returns {boolean} True if token is expired
   */
  isTokenExpired(token) {
    if (!token || token === 'cookie-auth') return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      const isExpired = Date.now() >= expiry;
      
      if (isExpired) {
        console.log('⏰ SecureStorage: Token has expired');
      }
      
      return isExpired;
    } catch (e) {
      console.error('❌ SecureStorage: Error checking token expiry:', e);
      return true; // Consider invalid tokens as expired
    }
  }

  /**
   * Sanitize user data to remove sensitive information
   * @param {Object} user 
   * @returns {Object} Sanitized user data
   */
  sanitizeUserData(user) {
    if (!user) return null;
    
    // Only keep essential fields needed for UI display
    const sanitizedUser = {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      avatar_url: user.avatar_url || null,
      client_type: user.client_type,
      is_super_admin: user.is_super_admin,  // Use RBAC field instead of deprecated is_admin
      phone: user.phone || '',
      timezone: user.timezone || null,
      // RBAC fields (needed for frontend authorization)
      roles: user.roles || [],
      primary_role: user.primary_role || null,
      permissions: user.permissions || [],
      // Professional role (agent/lawyer) requested at signup and awaiting KeyTa approval
      requested_role: user.requested_role || null,
      // Only include agent-specific fields if user is an agent
      ...(user.primary_role?.name === 'agent' && {
        license_number: user.license_number,
        brokerage: user.brokerage 
      })
    };
    
    return sanitizedUser;
  }

  /**
   * Set secure httpOnly cookie via backend API
   * @param {string} token 
   */
  async setSecureCookie(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/secure/set-secure-cookie`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
        credentials: 'include' // Important for cookies
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error setting secure cookie:', error);
      throw error; // Re-throw so the caller can handle it
    }
  }

  /**
   * Clear secure httpOnly cookie
   */
  async clearSecureCookie() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/secure/clear-secure-cookie`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error clearing secure cookie:', error);
    }
  }

  /**
   * Verify if the secure cookie is valid
   * @returns {Promise<boolean>} True if cookie is valid
   */
  async verifyCookie() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/secure/verify-cookie`, {
        method: 'GET',
        credentials: 'include'
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error verifying secure cookie:', error);
      return false;
    }
  }

  /**
   * Rotate the session using the httpOnly refresh cookie.
   * Backend sets a fresh access cookie (and refresh cookie) on success.
   * @returns {Promise<boolean>} True if the session was renewed
   */
  async refreshSession() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/secure/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) return false;

      // Only the sessionStorage fallback needs the raw token; cookie mode is done.
      if (sessionStorage.getItem(this.tokenKey)) {
        const data = await response.json().catch(() => null);
        if (data?.token) {
          sessionStorage.setItem(this.tokenKey, this.encryptToken(data.token));
        }
      }
      return true;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  }

  /**
   * @deprecated Refresh token is an httpOnly cookie set by the backend; nothing to store.
   */
  setRefreshToken() {
    sessionStorage.removeItem(this.refreshTokenKey);
  }

  /**
   * @deprecated Refresh token is not readable by JS. Use refreshSession().
   * @returns {null}
   */
  getRefreshToken() {
    return null;
  }

  /**
   * Clear refresh token (legacy sessionStorage cleanup)
   */
  clearRefreshToken() {
    sessionStorage.removeItem(this.refreshTokenKey);
  }

  /**
   * Synchronous method to get user data without cookie validation
   * Use with caution - only for cross-tab sync where we can't await
   * @returns {Object|null} User data or null if not found
   */
  getUserDataSync() {
    try {
      const userData = localStorage.getItem(this.userKey);
      if (userData) {
        return JSON.parse(userData);
      }
      return null;
    } catch (error) {
      console.error('❌ SecureStorage: Error retrieving user data sync:', error);
      return null;
    }
  }

  /**
   * Debug function to check current authentication state
   * Call from browser console: secureStorage.debugAuthState()
   */
  debugAuthState() {
    console.log('=== SecureStorage Debug Info ===');
    console.log('Token:', this.getToken());
    console.log('User Data:', this.getUserData());
    console.log('localStorage items:');
    console.log('- Token key:', localStorage.getItem(this.tokenKey));
    console.log('- User key:', localStorage.getItem(this.userKey));
    console.log('Memory storage:', this.memoryStorage);
    
    // Check token expiration if we have a token
    const token = this.getToken();
    if (token && token !== 'cookie-auth') {
      console.log('Token expiry check:', this.isTokenExpired(token) ? 'EXPIRED' : 'VALID');
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiryDate = new Date(payload.exp * 1000);
        console.log('Token expires at:', expiryDate.toLocaleString());
        console.log('Current time:', new Date().toLocaleString());
      } catch (e) {
        console.log('Could not parse token expiry');
      }
    }
    
    // Test cookie verification
    this.verifyCookie().then(valid => {
      console.log('Cookie verification result:', valid);
    }).catch(error => {
      console.log('Cookie verification error:', error);
    });
  }

  /**
   * Check if running in secure context
   * @returns {boolean}
   */
  isInSecureContext() {
    return this.isSecureContext;
  }


}

const secureStorage = new SecureStorage();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.secureStorage = secureStorage;
}

export default secureStorage;